import * as React from "react";
import { Static } from "@bizform/type/decorator/Static.decorator";
import {
    BaseStateModel,
    ComponentElement,
    ComponentElementBorderType,
    ComponentElementDisplayMode,
    ComponentElementStatic
} from "@bizform/type/model/component_element/ComponentElement.model";
import {
    ComponentCategory,
    ComponentElementManifest,
    ComponentElementProperty
} from "@bizform/type/model/component_element/ComponentElementManifest.model";
import { DomAttribute } from "@bizform/type/model/dom/DomAttribute.model";
import { WindowCommandbarItem } from "@bizform/type/model/data_set/WindowIconItem.model";
import { ComponentHelper, Debug } from "@bizform/helper/dist/js/bizflow/helper.min";
import { ComponentElementClass, ReactComponentElementInit } from "@bizform/core/dist/js/bizflow/core.min";
import { AbstractComponentElement } from "../../bf_component/AbstractComponentElement";
import { I18n } from "../../misc/model/I18n.model";


type Props = ComponentElementBorderType & {
    title?: string;
    model?: Array<WindowCommandbarItem>;
};

type WrappedCompState = Props & {
    // attribute
    // event
};

type State = BaseStateModel & {
    wrappedCompState: WrappedCompState;
};

/**
 * WindowPanel component element
 *
 * @since 0.0.1
 * @author Jongkwang Park
 * @class
 */
@ComponentElementClass()
@Static<ComponentElementStatic<string>>()
export class BfWindowPanel extends AbstractComponentElement<Props, State> {
    static readonly hidden: boolean = true;

    /* manifest */
    static readonly componentElementManifest: ComponentElementManifest = {
        uniqueId: "_n0IOlJxTpn5zQ12e141RczxtA_B9b6q",
        category: ComponentCategory.PANEL,
        label: I18n.layout.window.label,
        description: I18n.layout.window.description,
        icon: "",
        property: null
    };

    focus(): void {
    }

    setProperty(property: ComponentElementProperty): void {
        try {
            const newState = ComponentHelper.createStateWithProperties<State, null>(
                property,
                this);
            this.setState(newState);
        } catch (e) {
            Debug.console.error("setProperty", e);
        }
    }

    private _itemValueEvent(e: {originalEvent: React.MouseEvent<any>, item: WindowCommandbarItem}): void {
        // emit value
        e.originalEvent.preventDefault();
        const itemValue = e.item && (e.item as {itemValue}).itemValue;

        if (itemValue) {
            this.emitFormState(itemValue);
        }
    }

    emitFormState(value: string): void {
    }

    @ReactComponentElementInit(BfWindowPanel)
    static init(): ComponentElement<string> {
        return;
    }

    initDone(): void {
    }

    destroy(): void {
        this.destroyDone();
    }

    destroyDone(): void {
    }

    /* for React */
    props: Props;
    state: State;

    static defaultProps = {
    };

    private _ref: object;

    constructor(props?: Props) {
        super(props);

        this.state = {
            show: false,
            displayMode: ComponentElementDisplayMode.INIT,
            wrappedCompState: {
                model: []
            }
        };

        this._setRef = this._setRef.bind(this);
        this._itemValueEvent = this._itemValueEvent.bind(this);
    }

    componentWillUnmount(): void {
        this._ref = null;
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        Debug.console.error(error, errorInfo);
    }

    render(): React.ReactNode {
        const {
            show,
            displayMode
        } = this.state;

        if (!show) {
            return null;
        } else {
            switch (displayMode) {
                case ComponentElementDisplayMode.EDIT:
                case ComponentElementDisplayMode.READONLY:
                case ComponentElementDisplayMode.DISABLED:
                case ComponentElementDisplayMode.PRINT:
                    return this._render4ReadOnly();
                default:
                    return null;
            }
        }
    }

    /**
     * setProperty로 전달받은 model로 icon들을 렌더링 한다.
     *
     * @private
     */
    private _createIcons(): any {
        const model = this.state.wrappedCompState.model;
        const _this = this;

        if (model && Array.isArray(model)) {
            const iconContent = model.map((item, index) => {
                return (
                    <a key={index}
                       onClick={function (e) {
                            _this._itemValueEvent({originalEvent: e, item: item});
                        }}
                       style={{cursor: "pointer"}}
                    ><i className={item.icon}></i></a>
                );
            });

            return iconContent;
        } else {
            return null;
        }
    }

    private _defaultStr(valueStr: string, defaultStr: string): string {
        if (valueStr === null || valueStr === "")
            return defaultStr;
        else return valueStr;
    }

    private _render4ReadOnly(): React.ReactNode {
        const windowPanelStyle = {
            width: "100%",
            height: "100%",
            display: "flex"
        };
        const windowInnerBodyStyle = {
            flex: "1 1 100%",
            overflow: "auto"
        };

        const bfContainer = {[DomAttribute.CONTAINER.toString()]: ""};
        const iconContent = this._createIcons();
        const { title, borderTop, borderRight, borderBottom, borderLeft } = this.state.wrappedCompState;
        const border = { borderTop, borderRight, borderBottom, borderLeft };
        const className = ComponentHelper.makeBorderStyle("bf-window-panel", border);

        return (
            <div className={className}
                 style={{...windowPanelStyle, flexDirection: "column"}}
                 ref={this._setRef}
            >
                <div className="bf-panel-top" style={{position: "relative"}}>
                    <div className="title" style={{width: "100%"}}>{this._defaultStr(title, "\u00A0")}</div>
                    <div className="icon" style={{position: "absolute", right: 0, paddingRight: "5px", width: "100%"}}>{iconContent}</div>
                </div>
                <div style={windowInnerBodyStyle} {...bfContainer}></div>
            </div>
        );
    }

    private _setRef(ref): void {
        if (ref) {
            this._ref = ref;
            this.readyComponent();
        }
    }
}
